import { useState } from "react";
import Button from "@material-ui/core/Button";
import { useTranslation } from "react-i18next";
import apiRequest from "utils/apiRequestWithErrorCode";
import { isConflict } from "utils/apiRequestWithErrorCode";
import ProgressMessage from "./ProgressMessage";
import { ResponseOrganization, o365gateway } from "@dashboard-v3/api";
import useInterval from "utils/useInterval";
import { Box, Typography } from "@material-ui/core";
import { InstalledStatus } from "./InstalledStatus";
import { useSnackbar } from "notistack";
import useAuthPopup, {
  PopupClosed,
  ResponseFail,
  ResponseSuccess,
  wasClosedByUser,
} from "components/useAuthPopup";
import FlowSelectionDialog from "./FlowSelectionDialog";
import { captureException, captureMessage } from "utils/traceError";

import { Office365Gateway } from "../types";

type OperationProgress = o365gateway.OperationProgress;
type ConnectorType = o365gateway.ConnectorType;

enum Step {
  Idle = "IDLE",
  SelectFlow = "SELECT_FLOW",
  Auth = "AUTH",
  Progress = "PROGRESS",
  SavingChanges = "SAVING_CHANGES",
  End = "END",
  Fail = "FAIL",
}

type State = {
  step: Step;
  gateway?: Office365Gateway;
  saveAsDisabled?: boolean;
  progress?: OperationProgress;
  operationId?: string;
};

interface O365GatewayProps {
  gateway?: Office365Gateway;
}

export default function O365Gateway({ gateway }: O365GatewayProps) {
  const { t } = useTranslation("gateway");
  const { enqueueSnackbar } = useSnackbar();
  const { open } = useAuthPopup();
  const [state, setState] = useState<State>(newClearState(gateway));

  let showAddButton = state.step === Step.Idle && !state.gateway;

  let showInstalled =
    state.step === "END" ||
    (state.gateway &&
      state.step !== Step.Progress &&
      state.step !== Step.SavingChanges &&
      state.step !== Step.Fail);

  useInterval(listenProgress, state.step === Step.Progress ? 300 : null);

  async function openDialog(
    direction: ConnectorType[],
    saveAsDisabled: boolean
  ) {
    try {
      const response = await open({ url: "/auth/powershell/authorize" });
      if (response.success) {
        const { code } = response.payload;

        const created = await apiRequest<{ operationId: string }>(
          "POST",
          `/o365gateway`,
          {
            code,
            direction,
            enabled: !saveAsDisabled,
          }
        );

        setState(prev => ({
          ...prev,
          step: Step.Progress,
          progress: undefined,
          gateway: {
            enabled: !saveAsDisabled,
            direction,
          },
          saveAsDisabled,
          operationId: created.operationId,
        }));
      } else {
        if (wasClosedByUser(response) || userRejectsAuth(response)) return;

        enqueueSnackbar(t("authError"), { variant: "error" });
        captureMessage("An error ocurred in the authorization process", {
          extra: { response },
        });
      }
    } catch (error) {
      if (isConflict(error)) {
        enqueueSnackbar(t("O365Gateway.operationAlreadyActive"), {
          variant: "warning",
        });
        return;
      }
      captureException(error);
      enqueueSnackbar(t("authError"), { variant: "error" });
    }
  }

  async function updateOrganization() {
    try {
      await apiRequest<ResponseOrganization>("PATCH", "/organization", {
        office365Gateway: state.gateway,
      });

      await new Promise(resolve => setTimeout(resolve, 3000));
      setState(prev => ({ ...prev, step: Step.End }));
    } catch (error) {
      captureException(error);
      enqueueSnackbar(t("savedFail"), { variant: "error" });
    }
  }

  async function listenProgress() {
    try {
      const progress = await apiRequest<OperationProgress>(
        "GET",
        `/o365gateway/${state.operationId}`
      );

      if (!progress) return;

      if (progress.status === "completed") {
        setState(prev => ({
          ...prev,
          progress,
          step: Step.SavingChanges,
        }));
        return await updateOrganization();
      }

      if (progress.status === "failed") {
        captureMessage("O365 gateway operation failed", {
          extra: { operationId: state.operationId, error: progress.error },
        });
        return setState(prev => ({
          ...prev,
          progress,
          step: Step.Fail,
        }));
      }

      setState(prev => ({ ...prev, progress }));
    } catch (error) {
      captureException(error);
      setState(prev => ({ ...prev, step: Step.Fail }));
    }
  }

  function initConfig() {
    setState(prev => ({ ...prev, step: Step.SelectFlow }));
  }

  function onFlowSelect(direction: ConnectorType[], saveAsDisabled: boolean) {
    setState(prev => ({ ...prev, step: Step.Idle }));
    openDialog(direction, saveAsDisabled);
  }

  return (
    <>
      <div style={{ maxWidth: "70%" }}>
        <Typography variant="subtitle2" gutterBottom>
          {t("o365Label")}
        </Typography>
        <Typography variant="caption" color="textSecondary" gutterBottom>
          {t("O365Gateway.description")}
        </Typography>
      </div>
      {state.step === "SELECT_FLOW" && (
        <FlowSelectionDialog
          open={state.step === "SELECT_FLOW"}
          onClose={() => setState(newClearState(gateway))}
          onSelect={onFlowSelect}
        />
      )}
      <Box mt={2}>
        {showAddButton && (
          <Button
            variant="contained"
            color="default"
            size="small"
            disableElevation
            onClick={initConfig}
          >
            {t("O365Gateway.addConnector")}
          </Button>
        )}
        {(state.step === Step.Progress ||
          state.step === Step.SavingChanges) && (
          <ProgressMessage
            progress={state.progress}
            operationCompleted={state.step === Step.SavingChanges}
          />
        )}

        {state.step === Step.Fail && (
          <Box>
            <Typography variant="body2" color="error">
              {t("O365Gateway.operationFailed", { code: state.operationId })}
            </Typography>
            <Box mt={1}>
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={initConfig}
              >
                {t("O365Gateway.configureAgain")}
              </Button>
            </Box>
          </Box>
        )}

        {showInstalled && (
          <Box display="flex" gridColumnGap="20px" alignItems="center">
            <InstalledStatus gateway={state.gateway} />
            <Button
              variant="outlined"
              color="default"
              size="small"
              onClick={initConfig}
            >
              {t("O365Gateway.configureAgain")}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}

function userRejectsAuth(
  response: ResponseSuccess | ResponseFail | PopupClosed
): boolean {
  return "errorCode" in response && response.errorCode === "ACCESS_DENIED";
}

function newClearState(gateway?: Office365Gateway): State {
  const operationStatus = gateway?.operation?.status;
  const isRunning =
    operationStatus === "accepted" || operationStatus === "in_progress";

  if (isRunning) {
    return {
      step: Step.Progress,
      gateway,
      operationId: gateway.operation.id,
    };
  }

  if (operationStatus === "failed") {
    return {
      step: Step.Fail,
      gateway,
    };
  }

  return {
    step: gateway?.enabled ? Step.End : Step.Idle,
    gateway,
  };
}
